import { ExternalPolicy } from '@/types/PolicyCBO';
import * as XLSX from 'xlsx';
import { z, ZodTypeAny } from 'zod';
import { IsNullOrUndefined } from './Helpers';
import { SafeReadCell } from './Worksheets';

const boolFromYesNo = (x: unknown) => {
  if (typeof x === 'string') {
    return x.toLowerCase() === 'yes' || x.toLowerCase() === 'x';
  }
  return x;
};

const isNumberOrStatutory = (value: unknown) => {
  if (typeof value === 'undefined') {
    return true;
  } else if (typeof value === 'string') {
    return value.toLowerCase() === 'statutory' || value === '';
  } else {
    return typeof value === 'number';
  }
};

const policyColumnMap: Partial<Record<keyof ExternalPolicy, string>> = {
  accountName: 'B',
  description: 'C',
  insurerGroup: 'D',
  agencyName: 'E',
  coverageType: 'F',
  aggregateLimit: 'G',
  occurrenceLimit: 'H',
  attachmentPoint: 'I',
  retention: 'J',
  premium: 'K',
  effectiveDate: 'L',
  expirationDate: 'M',
  primaryLayer: 'N',
  sideAType: 'O',
  runoff: 'P',
  isQuotaShare: 'Q',
  quotaAmount: 'R',
  mainPolicyNumber: 'S',
  wholesaler: 'T'
};

const policySchema: Record<keyof typeof policyColumnMap, ZodTypeAny> = {
  accountName: z.string().optional(),
  description: z.string().default(''),
  insurerGroup: z.string(),
  agencyName: z.string().optional(),
  coverageType: z.string(),
  aggregateLimit: z.any().refine(isNumberOrStatutory, { message: 'Aggregate Limit must be a number or "Statutory"' }),
  occurrenceLimit: z.any().refine(isNumberOrStatutory, { message: 'Occurrence Limit must be a number or "Statutory"' }),
  attachmentPoint: z.preprocess(x => x === undefined ? 0 : x, z.coerce.number()),
  retention: z.coerce.number().optional(),
  premium: z.coerce.number().optional(),
  effectiveDate: z.coerce.date(),
  expirationDate: z.coerce.date(),
  primaryLayer: z.string(),
  sideAType: z.string().optional(),
  runoff: z.preprocess(boolFromYesNo, z.coerce.boolean()).default(false),
  isQuotaShare: z.preprocess(boolFromYesNo, z.coerce.boolean()).default(false),
  quotaAmount: z.preprocess(x => x === '' ? undefined : x, z.coerce.number().nullable().optional()),
  mainPolicyNumber: z.string().optional(),
  wholesaler: z.string().optional(),
} as never;

function transformRowToPolicy(worksheet: XLSX.WorkSheet, row: number): { policy?: ExternalPolicy, errors: string[] } {
  const policy: Partial<ExternalPolicy> = {};
  const errors: string[] = [];

  for (const key in policyColumnMap) {
    const typedKey = key as keyof typeof policyColumnMap;
    const cell = SafeReadCell(worksheet, `${policyColumnMap[typedKey]}${row + 1}`, policySchema[typedKey]);
    if (cell.error) {
      errors.push(cell.error);
    }
    const value = cell.value;
    policy[typedKey] = !IsNullOrUndefined(value) ? value as never : undefined;
  }

  // additional validation for Work Comp coverage type
  // For all coverage types that are not Work Comp
  if (policy.coverageType !== 'Work Comp') {
    if (typeof policy.aggregateLimit === 'string' && policy.aggregateLimit === 'Statutory') {
      errors.push('Aggregate limit cannot be "Statutory" for non-Work Comp coverage types');
    }

    if (typeof policy.occurrenceLimit === 'string' && policy.occurrenceLimit === 'Statutory') {
      errors.push('Occurrence limit cannot be "Statutory" for non-Work Comp coverage types');
    }
  } 
  // For Work Comp coverage type, set the limits to undefined
  else {
    policy.aggregateLimit = undefined;
    policy.occurrenceLimit = undefined;
  }

  if (errors.length > 0) {
    return { errors };
  }

  return { policy: policy, errors: [] } as never;
}


function isRowEmpty(worksheet: XLSX.WorkSheet, row: number): boolean {
  for (const key in policyColumnMap) {
    const cell = SafeReadCell(worksheet, `${policyColumnMap[key as keyof ExternalPolicy]}${row + 1}`, z.string().optional());
    if (cell.value) {
      return false;
    }
  }
  return true;
}

export const UNREADABLE_FILE_ERROR =
  'This file could not be read. Please confirm it is a valid .xlsx workbook and try again.';

export function TransformWorksheetToPolicies(sheetData: ArrayBuffer | null): { policies: ExternalPolicy[], errors: string[] } {
  const policies: ExternalPolicy[] = [];
  const errors: string[] = [];

  if (!sheetData) {
    return { policies: [], errors: [UNREADABLE_FILE_ERROR] };
  }

  // Cell-level problems already return through `errors` (see SafeReadCell), but the workbook
  // parse itself was unguarded: XLSX.read throws on a corrupt or non-xlsx file
  // ("Bad compressed size: ..."), and a workbook with no sheets leaves `worksheet` undefined.
  // Both used to escape as an unhandled exception.
  let worksheet: XLSX.WorkSheet | undefined;
  try {
    const workbook = XLSX.read(sheetData, { type: 'array' });
    worksheet = workbook.Sheets[workbook.SheetNames[0]];
  } catch {
    return { policies: [], errors: [UNREADABLE_FILE_ERROR] };
  }

  if (!worksheet || !worksheet['!ref']) {
    return { policies: [], errors: ['This workbook has no data. Please use the latest template and try again.'] };
  }

  const range = XLSX.utils.decode_range(worksheet['!ref']);

  // Check for deprecated "Layer ID" column in header row
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ c: col, r: 0 }); // Row 0 is the header row
    const cell = worksheet[cellAddress];
    const cellValue = cell?.w || cell?.v; // Get display value or raw value
    
    if (cellValue && typeof cellValue === 'string' && cellValue.trim().toLowerCase() === 'layer id') {
      return { 
        policies: [], 
        errors: ['This template appears to be outdated. Please download and use the latest version.']
      };
    }
  }

  // range end rows minus start rows plus 1
  const numberOfRows = range.e.r - range.s.r + 1;
  for (let i = 1; i < numberOfRows; i++) { // skip label row
    if (isRowEmpty(worksheet, i)) {
      break;
    }
    const result = transformRowToPolicy(worksheet, i);
    if (result.errors.length > 0) {
      errors.push(...result.errors);
    } else {
      policies.push(result.policy as ExternalPolicy);
    }
  }

  // XLSX.read does not reject everything that isn't a workbook -- a text file falls through to
  // its delimited-text reader and yields a sheet with no policy rows. Without this the upload
  // came back "valid" with nothing in it and the user could submit an empty request.
  if (errors.length === 0 && policies.length === 0) {
    return { policies: [], errors: ['No policy rows were found. Please use the latest template and try again.'] };
  }

  return { errors, policies: errors.length > 0 ? [] : policies };
}
